import { AxiosResponse } from 'axios';
import { useContext } from 'react';
import { AxiosContext } from '../../providers/axiosContext';
import { basicApiKey } from '../../conf';

export interface AuthService {
  login: (usr: string, psw: string) => Promise<AxiosResponse<any, any>>;
  register: (data: any) => Promise<AxiosResponse<any, any>>;
  forgotPassword: (email: string) => Promise<AxiosResponse<any, any>>;
  changePassword: (
    code: number,
    psw: string,
    email: string
  ) => Promise<AxiosResponse<any, any>>;
  validateCode: (
    userId: string,
    code: string
  ) => Promise<AxiosResponse<any, any>>;
  updatePassword: (
    userId: string,
    newPassword: string
  ) => Promise<AxiosResponse<any, any>>;
  confirmPassword: (oldPassword: string) => Promise<AxiosResponse<any, any>>;
  changePasswordLogged: (
    newPassword: string
  ) => Promise<AxiosResponse<any, any>>;
  externalLogin: (
    service: string,
    info: any
  ) => Promise<AxiosResponse<any, any>>;
  validateEmail: (email: string) => Promise<any>;
  findUserData: (accessToken?: string) => Promise<AxiosResponse<any, any>>;
}

export const useAuthService = (): AuthService => {
  const axiosContext = useContext(AxiosContext);

  const login = async (email: string, password: string) => {
    return axiosContext.newAuthAxios.post(
      '/api/auth/login/email',
      {
        email,
        password,
      },
      {
        headers: {
          'x-basic-api-key': basicApiKey,
        },
      }
    );
  };

  const findUserData = async (accessToken?: string) => {
    return axiosContext.newAuthAxios.get('/api/auth/users/me', {
      headers: accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : undefined,
    });
  };

  const externalLogin = (service: string, info: any) => {
    return axiosContext.newAuthAxios.post(`/api/auth/login/${service}`, info, {
      headers: {
        'x-basic-api-key': basicApiKey,
      },
    });
  };

  const register = async (data: any) => {
    const res = await axiosContext.newAuthAxios.post(
      '/api/auth/users/register',
      data,
      {
        headers: {
          'x-basic-api-key': basicApiKey,
        },
      }
    );

    return res;
  };

  const forgotPassword = async (email: string) => {
    return axiosContext.newAuthAxios.post(
      '/api/auth/users/forgot/password',
      {
        email,
      },
      {
        headers: {
          'x-basic-api-key': basicApiKey,
        },
      }
    );
  };

  const changePassword = async (
    code: number,
    newPassword: string,
    email: string
  ) => {
    return axiosContext.publicAxios.post('/web/users/change-password', {
      code,
      newPassword,
      email,
    });
  };

  const confirmPassword = async (oldPassword: string) => {
    return axiosContext.authAxios.post('/web/users/confirm-password', {
      password: oldPassword,
    });
  };

  const changePasswordLogged = async (newPassword: string) => {
    return axiosContext.authAxios.post('/web/users/change-password-logged', {
      newPassword,
    });
  };

  const validateCode = async (userId: string, code: string) => {
    return axiosContext.newAuthAxios.post(
      '/api/auth/validation-codes/validate',
      {
        userId,
        code,
        type: 'password_recovery',
      },
      {
        headers: {
          'x-basic-api-key': basicApiKey,
        },
      }
    );
  };

  const updatePassword = async (userId: string, newPassword: string) => {
    return axiosContext.newAuthAxios.post(
      '/api/auth/users/change/password',
      {
        userId,
        newPassword,
      },
      {
        headers: {
          'x-basic-api-key': basicApiKey,
        },
      }
    );
  };

  const validateEmail = (email: string) => {
    return axiosContext.publicAxios.get(
      `/validation-utils/emails?email=${email}`
    );
  };

  return {
    login,
    register,
    changePassword,
    forgotPassword,
    validateCode,
    updatePassword,
    confirmPassword,
    changePasswordLogged,
    externalLogin,
    validateEmail,
    findUserData,
  };
};
